import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';
import { Building2, MapPin, Users, Globe, Linkedin, TrendingUp, ExternalLink, Link2, Loader2 } from 'lucide-react';
import { AIEnrichedBadge } from './AIEnrichedBadge';
import { useToast } from '@/hooks/use-toast';

interface CompanyDetails {
  id: string;
  name: string;
  industry?: string | null;
  website_url?: string | null;
  linkedin_url?: string | null;
  headquarters_city?: string | null;
  headquarters_country?: string | null;
  employee_count?: number | null;
  stock_ticker?: string | null;
  main_business?: string | null;
  crm_status?: string | null;
  ai_enriched_at?: string | null;
}

interface CompanyHoverCardProps {
  companyId?: string | null;
  companyName?: string | null;
  experienceId?: string | null;
  className?: string;
  onCompanyLinked?: (companyId: string) => void;
}

export const CompanyHoverCard = ({ companyId, companyName, experienceId, className, onCompanyLinked }: CompanyHoverCardProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [company, setCompany] = useState<CompanyDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [linkedCompanyId, setLinkedCompanyId] = useState<string | null>(companyId || null);

  const displayName = companyName || 'Unknown Company';

  const fetchCompanyDetails = async () => {
    if (fetched || !linkedCompanyId) return;
    
    setLoading(true);
    try {
      const { data } = await supabase
        .from('companies')
        .select('id, name, industry, website_url, linkedin_url, headquarters_city, headquarters_country, employee_count, stock_ticker, main_business, crm_status, ai_enriched_at')
        .eq('id', linkedCompanyId)
        .single();
      
      if (data) setCompany(data);
    } finally {
      setLoading(false);
      setFetched(true);
    }
  };

  const getCrmStatusVariant = (status?: string | null) => {
    switch (status) {
      case 'active_client': return 'default';
      case 'prospect': return 'secondary';
      case 'churned': return 'destructive';
      default: return 'outline';
    }
  };

  // Create company and link to experience
  const handleCreateAndLink = async () => {
    if (!companyName || !experienceId) return;
    
    setLinking(true);
    try {
      // Check if company already exists
      const { data: existingCompany } = await supabase
        .from('companies')
        .select('id')
        .ilike('name', companyName)
        .maybeSingle();

      let newCompanyId: string;

      if (existingCompany) {
        newCompanyId = existingCompany.id;
      } else {
        // Create new company
        const { data: newCompany, error: createError } = await supabase
          .from('companies')
          .insert({ name: companyName })
          .select('id')
          .single();

        if (createError) throw createError;
        newCompanyId = newCompany.id;
      }

      // Link to experience
      const { error: linkError } = await supabase
        .from('candidate_experience')
        .update({ company_id: newCompanyId })
        .eq('id', experienceId);

      if (linkError) throw linkError;

      setLinkedCompanyId(newCompanyId);
      toast({ title: existingCompany ? 'Linked to existing company' : 'Company created and linked' });
      onCompanyLinked?.(newCompanyId);
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setLinking(false);
    }
  };

  // If no company_id and no company_name, show plain text
  if (!linkedCompanyId && !companyName) {
    return <span className={className}>Unknown Company</span>;
  }

  // If no linked company yet, show with option to create/link
  if (!linkedCompanyId) {
    return (
      <HoverCard openDelay={200} closeDelay={100}>
        <HoverCardTrigger asChild>
          <span className={`text-muted-foreground cursor-pointer inline-flex items-center gap-1 hover:text-primary ${className || ''}`}>
            {displayName}
            <Link2 className="h-3 w-3 opacity-50" />
          </span>
        </HoverCardTrigger>
        <HoverCardContent className="w-72" align="start">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-muted-foreground" />
              <div>
                <h4 className="font-semibold">{displayName}</h4>
                <p className="text-xs text-muted-foreground">Not linked to company module</p>
              </div>
            </div>
            {experienceId && (
              <Button 
                size="sm" 
                className="w-full" 
                onClick={handleCreateAndLink}
                disabled={linking}
              >
                {linking ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Link2 className="h-4 w-4 mr-1" />}
                Create & Link Company
              </Button>
            )}
          </div>
        </HoverCardContent>
      </HoverCard>
    );
  }

  return (
    <HoverCard openDelay={200} closeDelay={100}>
        <HoverCardTrigger asChild>
          <span
            className={`text-primary hover:underline cursor-pointer inline-flex items-center gap-1 ${className || ''}`}
            onMouseEnter={fetchCompanyDetails}
            onClick={() => navigate(`/recruiter/companies?id=${linkedCompanyId}`)}
          >
            {displayName}
            <ExternalLink className="h-3 w-3 opacity-50" />
          </span>
        </HoverCardTrigger>
      <HoverCardContent className="w-80" align="start">
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        ) : company ? (
          <div className="space-y-3">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-muted-foreground" />
                <div>
                  <h4 className="font-semibold">{company.name}</h4>
                  {company.industry && (
                    <p className="text-xs text-muted-foreground">{company.industry}</p>
                  )}
                </div>
              </div>
              {company.ai_enriched_at && <AIEnrichedBadge size="sm" />}
            </div>

            {company.crm_status && (
              <Badge variant={getCrmStatusVariant(company.crm_status)} className="text-xs">
                {company.crm_status.replace('_', ' ')}
              </Badge>
            )}

            <div className="space-y-1.5 text-sm">
              {(company.headquarters_city || company.headquarters_country) && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin className="h-3.5 w-3.5" />
                  <span>{[company.headquarters_city, company.headquarters_country].filter(Boolean).join(', ')}</span>
                </div>
              )}

              {company.employee_count && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Users className="h-3.5 w-3.5" />
                  <span>{company.employee_count.toLocaleString()} employees</span>
                </div>
              )}

              {company.stock_ticker && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <TrendingUp className="h-3.5 w-3.5" />
                  <span>{company.stock_ticker}</span>
                </div>
              )}

              {company.website_url && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Globe className="h-3.5 w-3.5" />
                  <a 
                    href={company.website_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:text-primary truncate"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {company.website_url.replace(/^https?:\/\//, '')}
                  </a>
                </div>
              )}

              {company.linkedin_url && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Linkedin className="h-3.5 w-3.5" />
                  <a 
                    href={company.linkedin_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:text-primary"
                    onClick={(e) => e.stopPropagation()}
                  >
                    LinkedIn Profile
                  </a>
                </div>
              )}
            </div>

            {company.main_business && (
              <p className="text-xs text-muted-foreground border-t pt-2">
                {company.main_business}
              </p>
            )}

            <div className="text-xs text-muted-foreground border-t pt-2">
              Click to view full company profile
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Company details not found</p>
        )}
      </HoverCardContent>
    </HoverCard>
  );
};
